import { DateTime, Interval } from 'luxon';

import { datasetById } from '../dataset';
import { entityById, getEntityByIdWithMetrics } from '../entity';
import { gridStateById } from '../gridState';
import { schemaForGridReport } from '../report';
import { platform } from '../transportWrapper';

import {
  IDataset,
  IGridQueryParams,
  IGridState,
  ILegacyGridReportSchema,
  ILegacyGridReportSchemaInfo,
  ILegacyReport,
  ILegacySavedView,
  ITransactionsQueryResponse
} from '../types';

export const hasYearOnlyData = (dataset: IDataset) => {
  let hasFinancialYear = false;
  let hasFinancialPeriod = false;
  if (dataset && dataset.column_mapping) {
    Object.keys(dataset.column_mapping).forEach((key) => {
      if (dataset.column_mapping[key].special_types) {
        if (dataset.column_mapping[key].special_types.indexOf('financial_year') >= 0) {
          hasFinancialYear = true;
        }
        if (dataset.column_mapping[key].special_types.indexOf('financial_period') >= 0) {
          hasFinancialPeriod = true;
        }
      }
    });
  }

  return hasFinancialYear && !hasFinancialPeriod;
};

const getDateFilter = (gridState: IGridState, schema: ILegacyGridReportSchemaInfo) => {
  return gridState.query && gridState.query.filter && gridState.query.filter[schema.meta.date_column];
};

export const getGridQueryBreakdown = (
  breakdown: string,
  breakdownColumns: string,
  schemaColumns: ILegacyGridReportSchema
): string => {
  if (!breakdownColumns && schemaColumns.some((schemaColumn) => schemaColumn.name === breakdown)) {
    return breakdown;
  }

  //filters out hidden columns to map report app's logic
  //tslint:disable-next-line:max-line-length
  //https://github.com/OpenGov/opengov/blob/master/source/javascript/apps/reporting-classic-app/javascripts/stores/grid_report_store.ts#L99
  const filterBreakdownColumns = (breakdownCols: string, schemaCols: ILegacyGridReportSchema): string[] => {
    if (!breakdownCols) return [];
    const schemaColumnsNames = schemaCols.map((schemaColumn) => schemaColumn.name);
    return breakdownCols.split(',').filter((breakdownColumn) => {
      return schemaColumnsNames.indexOf(breakdownColumn) !== -1;
    });
  };
  const filteredColumns = filterBreakdownColumns(breakdownColumns, schemaColumns);
  return filteredColumns.indexOf(breakdown) !== -1 ? breakdown : filteredColumns[0];
};

const parseDate = (date: string) =>
  DateTime.fromJSDate(
    new Date(
      Number(date.toString().substring(0, 4)), // Year
      Number(date.toString().substring(5, 7)) - 1, // Month
      Number(date.toString().substring(8, 10)) // Day
    )
  );
  

export const getMaxGridTimePeriod = (schema: ILegacyGridReportSchemaInfo) =>
  parseDate(schema.schema.find((col) => col.name === schema.meta.date_column).min_value).until(
    parseDate(schema.schema.find((col) => col.name === schema.meta.date_column).max_value)
  );

export const getGridTimePeriod = (
  gridState: IGridState,
  schema: ILegacyGridReportSchemaInfo,
  maxGridTimePeriod: Interval
) => {
  const dateFilter = getDateFilter(gridState, schema);

  const minVal = maxGridTimePeriod.start;
  const maxVal = maxGridTimePeriod.end;
  const ge = dateFilter && dateFilter.ge ? parseDate(dateFilter.ge) : minVal;
  const le = dateFilter && dateFilter.le ? parseDate(dateFilter.le) : maxVal;
  const eq = dateFilter && dateFilter.eq && parseDate(dateFilter.eq);
  const range = DateTime.max(ge, minVal).until(DateTime.min(le, maxVal));
  return eq && range.contains(eq) ? eq.until(eq) : range;
};

export const getGridYearMonthPairs = (gridTimePeriod: Interval) => {
  const gridYearMonthPairs = [];
  let current = gridTimePeriod.start;

  while (gridTimePeriod.end.toFormat('yyyy-MM') >= current.toFormat('yyyy-MM')) {
    gridYearMonthPairs.push({
      year: current.year,
      month: current.month
    });
    current = current.plus({ months: 1 });
  }

  if (gridYearMonthPairs.length) {
    gridYearMonthPairs[0].day = gridTimePeriod.start.day;
    gridYearMonthPairs[gridYearMonthPairs.length - 1].day = gridTimePeriod.end.day;
  }

  return gridYearMonthPairs;
};

export const getTransactionsLimit =
  (dbParallelCapable: boolean, isSummaryTableQuery: boolean, limit: number): number => {
  // Why 2048 ?
  // tslint:disable-next-line:max-line-length
  // https://github.com/OpenGov/opengov/blob/3a3288ff7c68f2f42ca3c1b21dd96e6c27ba4c7c/source/javascript/apps/reporting-classic-app/javascripts/query_planners/visual_aggregate_planner.ts#L33
  const POSTGRES_LIMIT = 2048;
  return isSummaryTableQuery && !dbParallelCapable ? POSTGRES_LIMIT : limit;
};

const polyfillGridState = (rawGridState: IGridState, schema: ILegacyGridReportSchemaInfo) => ({
  ...rawGridState,
  query: rawGridState.query || {},
  visualization: {
    ...(rawGridState.visualization || ({} as IGridState['visualization'])),
    mapAmountColumn: rawGridState.visualization.mapAmountColumn || schema.meta.amount_column,
    breakdown:
      rawGridState.visualization.breakdown ||
      schema.schema
        .filter((column) => {
          return column.visible && DISALLOWED_COLUMN_TYPE_BREAKDOWNS.indexOf(column.type) === -1;
        })
        .map((column) => column.name)[0]
  }
});

const DISALLOWED_COLUMN_TYPE_BREAKDOWNS = ['monetary', 'date', 'numeric'];

export const transactionsQuery = async (
  report: ILegacyReport,
  gridQueryParams: IGridQueryParams,
  clientOptions?: RequestInit
) =>
  platform
    .options(clientOptions, true)
    .url(`/api/transactions/v2/query/${report.transaction_id}`)
    .json(gridQueryParams)
    .post()
    .json<ITransactionsQueryResponse>();

export const performGridDataQuery = async ({
  savedView,
  report,
  clientOptions,
  serveOld,
  queryDataBy,
  isSummaryTableQuery = false
}: {
  savedView: ILegacySavedView;
  report: ILegacyReport;
  clientOptions?: RequestInit;
  serveOld?: boolean;
  queryDataBy?: 'month' | 'year';
  isSummaryTableQuery?: boolean;
}) => {
  if (report.report_type !== 'grid') {
    return Promise.reject({
      error: `Expected a Grid Report: received a report of type '${JSON.stringify(report.report_type)}'`
    });
  }

  return Promise.all([
    gridStateById(savedView.view_state.query, clientOptions),
    schemaForGridReport(report, clientOptions),
    entityById(report.entity_id, clientOptions),
    getEntityByIdWithMetrics(report.entity, clientOptions),
    datasetById(report.entity_id, report.transaction_id, clientOptions)
  ]).then(async ([rawGridState, schema, entity, entityWithMetrics, dataset]) => {
    const gridState: IGridState = polyfillGridState(rawGridState, schema);

    const yearOnlyData = hasYearOnlyData(dataset);

    const groupByParams = {
      column: schema.meta.date_column,
      fiscal_year_start_month: entity.fiscal_year_start_month
    };
    const { db_parallel_capable: dbParallelCapable = false } = schema?.meta;
    // Limits the number of transactions returned in a month
    const limit = getTransactionsLimit(dbParallelCapable, isSummaryTableQuery, gridState.visualization.limit);

    const amountColumnName =
      gridState.visualization.amountColumn || gridState.visualization.mapAmountColumn || schema.meta.amount_column;

    const baseGridQueryParamsDefaults = {
      count: true,
      fields: [amountColumnName],
      group: getGridQueryBreakdown(
        gridState.visualization.breakdown,
        gridState.visualization.breakdownColumns,
        schema.schema
      ),
      limit,
      remaining: true,
      total: true
    };

    const maxGridTimePeriod = getMaxGridTimePeriod(schema);

    const gridTimePeriod = getGridTimePeriod(gridState, schema, maxGridTimePeriod);

    const baseGridQueryParams = {
      ...baseGridQueryParamsDefaults,
      ...gridState.query,
      serveOld
    };

    const gridTimePeriodExactlyOneYear = gridTimePeriod.start.plus({ years: 1 }).equals(gridTimePeriod.end);
    const gridTimePeriodFiscalYearAligned = gridTimePeriod.start.month === entity.fiscal_year_start_month;
    const dateFilterDefined = getDateFilter(gridState, schema);

    const gridQueryParams: IGridQueryParams = {
      ...baseGridQueryParams,
      sort: [{ [`${amountColumnName}_sum`]: 'desc' }],
      filter: baseGridQueryParams.filter
    };

    const gridYearMonthPairs = getGridYearMonthPairs(gridTimePeriod);

    /*
    Querying w/ group_by_year_month will return all the transactions for all available year month pairs
    Querying w/ group_by_month will return all the transactions for all months only in the `fiscal_year`
    defined in gridQueryParams
    Querying w/ group_by_fiscal_year will return all the transactions aggregated in fiscal years
    */
    if (queryDataBy === 'year') {
      gridQueryParams.group_by_fiscal_year = groupByParams;
    } else if (queryDataBy === 'month') {
      gridQueryParams.group_by_year_month = { ...groupByParams, year_month_pairs: gridYearMonthPairs };
    } else if (gridTimePeriodExactlyOneYear && !yearOnlyData) {
      // If the user does not define a date filter
      // We should not assume the user desired the data to be grouped only by month
      if (gridTimePeriodFiscalYearAligned && dateFilterDefined) {
        gridQueryParams.group_by_month = { ...groupByParams, fiscal_year: gridTimePeriod.start.year };
      } else {
        gridQueryParams.group_by_year_month = { ...groupByParams, year_month_pairs: gridYearMonthPairs };
      }
    } else if (
      gridTimePeriod.end.diff(gridTimePeriod.start, 'days').days <= 365 &&
      gridYearMonthPairs.length &&
      !yearOnlyData
    ) {
      gridQueryParams.group_by_year_month = { ...groupByParams, year_month_pairs: gridYearMonthPairs };
    } else {
      gridQueryParams.group_by_fiscal_year = groupByParams;
    }

    return transactionsQuery(report, gridQueryParams, clientOptions).then((queryResponse) => {
      const fiscalYearStartMonth =
        entityWithMetrics && entityWithMetrics.facts && entityWithMetrics.facts.fiscal_year_start_month;

      //map calendar years to fiscal years when querying by month
      if (queryDataBy === 'month' && fiscalYearStartMonth && fiscalYearStartMonth !== 1) {
        queryResponse.transactions = queryResponse.transactions.map((entry) => {
          if (entry.month >= fiscalYearStartMonth) {
            entry.year++;
          }
          return entry;
        });
      }
      return {
        dataUpdatedAt: dataset.data_updated_at,
        queryResponse,
        gridState,
        schema,
        entity,
        gridQueryParams,
        amountColumn: schema.schema.find((c) => c.name === amountColumnName),
        maxGridTimePeriod,
        entityWithMetrics,
        yearOnlyData
      };
    });
  });
};
